import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, Container, Stack, Typography, Button, Alert, CircularProgress, Stepper, Step, StepIndicator } from '@mui/joy';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from 'context/SessionContext';
import { createUser, getCurrentUser, upsertEmployerPosition, getEmployer, getEmployerPositions } from '../../../apiconfig/api';
import CreateAccountStep from './components/CreateAccountStep';
import CreatePositionStep from './components/CreatePositionStep';
import PaymentStep from './components/PaymentStep';
import SharePositionStep from './components/SharePositionStep';
import { Check } from '@mui/icons-material';

const steps = [
  {
    id: 1,
    title: 'Employer Info',
    description: 'Set up your employer account to get started'
  },
  {
    id: 2,
    title: 'Job Info',
    description: 'Define the role you want to fill'
  },
  {
    id: 3,
    title: 'Payment',
    description: 'Choose your plan and enter payment information'
  },
  {
    id: 4,
    title: 'Share',
    description: 'Your position is live! Share it with people'
  }
];

interface WorkflowData {
  // Account data
  companyName: string;
  location: string;
  fullName: string;
  email: string;
  password: string;
  
  // Position data
  jobTitle: string;
  visibility: 'public' | 'unlisted' | 'inactive';
  isPartTime: boolean;
  isFullTime: boolean;
  description: string;
  
  // Payment data
  selectedPlan: string;
  paymentMethod: string;
  promoCode?: string;
  
  // Generated data
  positionId?: string;
  employerId?: string;
  positionSlug?: string;
  employerSlug?: string;
}

const PostJobWorkflow: React.FC = () => {
  const navigate = useNavigate();
  const { sessionEmployerId, isLoggedIn, setSessionEmployerId } = useSession();
  
  // Check if there's browser history to go back to
  const [hasHistory, setHasHistory] = useState(false);
  
  useEffect(() => {
    // Check if we can go back (there's history)
    setHasHistory(window.history.length > 1);
  }, []);
  
  // Determine starting step based on login status
  const getInitialStep = () => {
    if (isLoggedIn && sessionEmployerId) {
      return 2; // Skip account creation if already logged in
    }
    return 1;
  };
  
  // Workflow state
  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
  const [hasExistingPositions, setHasExistingPositions] = useState(false);
  const [debouncedPromoCode, setDebouncedPromoCode] = useState('');
  const [employerPlan, setEmployerPlan] = useState<{plan: string, planExpiresAt: string} | null>(null);
  const [hasActiveUnlimitedPlan, setHasActiveUnlimitedPlan] = useState(false);
  
  // Form data
  const [workflowData, setWorkflowData] = useState<WorkflowData>({
    companyName: '',
    location: '',
    fullName: '',
    email: '',
    password: '',
    jobTitle: '',
    visibility: 'public',
    isPartTime: false,
    isFullTime: false,
    description: '',
    selectedPlan: '',
    paymentMethod: '',
    promoCode: ''
  });

  const updateWorkflowData = (updates: Partial<WorkflowData>) => {
    setWorkflowData(prev => ({ ...prev, ...updates }));
  };

  // Create position after successful payment/validation
  const createPosition = async () => {
    if (!sessionEmployerId || !workflowData.jobTitle || (!workflowData.isFullTime && !workflowData.isPartTime) || !workflowData.description) {
      throw new Error("Missing required position data");
    }

    try {
      setLoading(true);
      setError(null);

      // Get employer data to get the slug
      const employerResponse = await getEmployer(sessionEmployerId);
      const employerSlug = employerResponse.data.employer.slug;
      
      const id = uuidv4();
      
      const payload = {
        displayNameOverride: workflowData.jobTitle,
        visibility: workflowData.visibility,
        isPartTime: workflowData.isPartTime,
        isFullTime: workflowData.isFullTime,
        isHourly: false,
        isSalary: false,
        description: workflowData.description,
        isActive: true // Set to active since payment is confirmed
      };

      const response = await upsertEmployerPosition(sessionEmployerId, id, payload);
      const positionSlug = response.data.employerPosition.slugOverride;
      
      // Store the slugs in workflow data for URL generation
      setWorkflowData(prev => ({
        ...prev,
        positionId: id,
        positionSlug: positionSlug,
        employerSlug: employerSlug
      }));

      console.log('Position created successfully:', { id, positionSlug, employerSlug });
      
    } catch (err: any) {
      console.error("Error creating position:", err);
      throw new Error(err.message || "Failed to create position. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Check for existing positions when employer ID is available
  useEffect(() => {
    const checkExistingPositions = async () => {
      if (!sessionEmployerId) return;
      
      try {
        const positionsResponse = await getEmployerPositions(sessionEmployerId, { per_page: 1 }, true);
        const existingPositions = positionsResponse.data?.employerPositions || [];
        setHasExistingPositions(existingPositions.length > 0);
      } catch (err) {
        console.error('Error checking existing positions:', err);
        // Don't set error state, just assume they have positions to be safe
        setHasExistingPositions(true);
      }
    };

    checkExistingPositions();
  }, [sessionEmployerId]);

  // Check employer plan status
  useEffect(() => {
    const checkEmployerPlan = async () => {
      if (!sessionEmployerId) return;
      
      try {
        const employerResponse = await getEmployer(sessionEmployerId);
        const employer = employerResponse.data.employer;
        
        setEmployerPlan({
          plan: employer.plan,
          planExpiresAt: employer.planExpiresAt
        });

        // Check if they have an active unlimited plan
        const isUnlimitedPlan = employer.plan === 'unlimited-posts-2025-10';
        const isActivePlan = employer.planExpiresAt && new Date(employer.planExpiresAt) > new Date();
        
        setHasActiveUnlimitedPlan(isUnlimitedPlan && isActivePlan);
        
        console.log('Employer plan check:', {
          plan: employer.plan,
          planExpiresAt: employer.planExpiresAt,
          isUnlimitedPlan,
          isActivePlan,
          hasActiveUnlimitedPlan: isUnlimitedPlan && isActivePlan
        });
      } catch (err) {
        console.error('Error checking employer plan:', err);
        // Don't set error state, just assume no unlimited plan
        setHasActiveUnlimitedPlan(false);
      }
    };

    checkEmployerPlan();
  }, [sessionEmployerId]);

  // Debounce the promo code for proceed button logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPromoCode(workflowData.promoCode || '');
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [workflowData.promoCode]);

  const handleNext = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Handle step-specific logic here
      switch (currentStep) {
        case 1:
          // Create user and employer account
          try {
            const userResponse = await createUser({
              fullName: workflowData.fullName,
              email: workflowData.email,
              password: workflowData.password,
              companyName: workflowData.companyName,
              companyAddress: workflowData.location,
            });

            // Store the token in localStorage
            if (userResponse.data.token) {
              localStorage.setItem('session_token', userResponse.data.token);
            }

            // Set sessionEmployerId after account creation
            try {
              const userResp = await getCurrentUser();
              const user = userResp.data.user;
              // If user has an employers array, use the first one
              if (user.employers && Array.isArray(user.employers) && user.employers.length > 0) {
                setSessionEmployerId(user.employers[0].uuid);
                // Store the employer ID in workflow data for later use
                setWorkflowData(prev => ({ ...prev, employerId: user.employers[0].uuid }));
              }
            } catch (e) {
              console.error('Error getting user data after account creation:', e);
              // Don't throw here, account was created successfully
              setSessionEmployerId(null);
            }

            console.log('Account created successfully');
          } catch (accountError: any) {
            console.error('Error creating account:', accountError);
            
            // Extract error message from backend response (same pattern as SignUp page)
            let errorMsg = 'Account creation failed. Please try again.';
            
            if (accountError.message) {
              errorMsg = accountError.message;
            } else if (accountError.response?.data?.message) {
              errorMsg = accountError.response.data.message;
            } else if (typeof accountError === 'string') {
              errorMsg = accountError;
            }
            
            throw new Error(errorMsg);
          }
          break;
        case 2:
          // Review step - just validate data, don't create position yet
          if ( !sessionEmployerId 
            || !workflowData.jobTitle 
            || (!workflowData.isFullTime && !workflowData.isPartTime) 
            || !workflowData.description ) {
            setError("Please complete all required fields.");
            return;
          }
          
          console.log('Position data validated, proceeding to payment...');
          break;
        case 3:
          // Check if valid promo code is applied
          const isValidPromoCode = debouncedPromoCode === 'FIRSTPOSTFREE' && !hasExistingPositions;
          
          if (hasActiveUnlimitedPlan || isValidPromoCode) {
            // Create position for valid promo code and skip payment processing
            console.log('Valid promo code applied, creating position and skipping payment');
            try {
              await createPosition();
              // Proceed directly to next step
              break;
            } catch (err: any) {
              setError(err.message);
              return;
            }
          } else {
            // Trigger payment submission for regular payment
            setIsSubmittingPayment(true);
            return; // Don't proceed to next step yet, PaymentForm will handle it
          }
          break;
        case 4:
          console.log('Position is live!');
          navigate(getPositionUrl())
          break;
        default:
          break;
      }
      
      // Move to next step
      if (currentStep < steps.length) {  
        setCurrentStep(currentStep + 1);
      }
      
    } catch (err: any) {
      console.error('Error in workflow step:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Step 3: Back button - go to previous step
    if (currentStep === 3) {
      setCurrentStep(currentStep - 1);
      return;
    }
    
    // Steps 1 & 2: Cancel button - go back to previous page if there's history
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate('/hiring/candidates');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CreateAccountStep 
            data={workflowData}
            onUpdate={updateWorkflowData}
          />
        );
      case 2:
        return (
          <CreatePositionStep 
            data={workflowData}
            onUpdate={updateWorkflowData}
          />
        );
      case 3:
        // Show different content for unlimited plan users
        if (hasActiveUnlimitedPlan) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 2 }}>
                Payment Complete
              </Typography>
              <Typography level="body-lg" sx={{ color: '#7B7F85', mb: 3 }}>
                You have an active unlimited plan. No payment required for this job posting.
              </Typography>
              <Box sx={{ 
                p: 3, 
                backgroundColor: '#F0F8FF', 
                borderRadius: '12px', 
                border: '2px solid #E3F2FD',
                maxWidth: '400px',
                mx: 'auto'
              }}>
                <Typography level="body-sm" sx={{ color: '#2e2d7e', fontWeight: 600 }}>
                  Plan: Unlimited
                </Typography>
                <Typography level="body-sm" sx={{ color: '#7B7F85' }}>
                  Expires: {employerPlan?.planExpiresAt ? new Date(employerPlan.planExpiresAt).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
            </Box>
          );
        }
        
        return (
          <PaymentStep 
            data={workflowData}
            onUpdate={updateWorkflowData}
            onNext={handlePaymentSuccess}
            isSubmitting={isSubmittingPayment}
            onFormReady={handlePaymentFormReady}
          />
        );
      case 4:
        return (
          <SharePositionStep 
            data={workflowData}
            onUpdate={updateWorkflowData}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return workflowData.companyName && workflowData.location && workflowData.fullName && workflowData.email && workflowData.password;
      case 2:
        return workflowData.jobTitle && (workflowData.isFullTime || workflowData.isPartTime) && workflowData.description.trim();
      case 3:
        // Payment step: form is ready OR valid promo code is applied OR active unlimited plan
        const isValidPromoCode = debouncedPromoCode === 'FIRSTPOSTFREE' && !hasExistingPositions;
        return isPaymentFormReady || isValidPromoCode || hasActiveUnlimitedPlan;
      case 4:
        return true; // Final step
      default:
        return false;
    }
  };

  const handlePaymentFormReady = React.useCallback((isReady: boolean) => {
    setIsPaymentFormReady(isReady);
  }, []);

  const handlePaymentSuccess = async () => {
    try {
      setIsSubmittingPayment(false);
      console.log('Payment successful, creating position...');
      
      // Create position after successful payment
      await createPosition();
      
      // Move to next step after successful position creation
      if (currentStep < steps.length) {  
        setCurrentStep(currentStep + 1);
      }
    } catch (err: any) {
      console.error('Error creating position after payment:', err);
      setError(err.message || 'Payment successful but failed to create position. Please try again.');
      setIsSubmittingPayment(false);
      setLoading(false);
    }
  };

  const getButtonText = () => {
    switch (currentStep) {
      case 1:
        return 'Create Account';
      case 2:
        return 'Post Job';
      case 3:
        // Show "Next" if payment is not required (unlimited plan or valid promo code)
        const isValidPromoCode = debouncedPromoCode === 'FIRSTPOSTFREE' && !hasExistingPositions;
        if (hasActiveUnlimitedPlan || isValidPromoCode) {
          return 'Next';
        }
        return 'Submit Payment';
      default:
        return 'View Job';
    }
  };

  const getPositionUrl = () => {
    // Use the actual slugs from the workflow data if available
    const employerSlug = workflowData.employerSlug || 'company';
    const jobSlug = workflowData.positionSlug || 'position';
    return `/hiring/${employerSlug}/${jobSlug}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: 4 }}>
      <Container maxWidth="md">
        <Stack spacing={4}>
          {/* Header */}
          <Box sx={{ textAlign: 'center' }} >
            <Typography level="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Post a New Job
            </Typography>
            <Stepper sx={{ mb: 2, maxWidth: { xs: '300px', md: '650px' }, mx: 'auto' }}>
              {steps.map((step) => (
                <Step
                  indicator={
                    currentStep > step.id || (step.id === 3 && hasActiveUnlimitedPlan) ? (
                      <StepIndicator variant="solid" color="primary">
                        <Check />
                      </StepIndicator>
                    ) : (
                      <StepIndicator variant={currentStep === step.id ? "solid" : "outlined"} color="primary">
                        {step.id}
                      </StepIndicator>
                    )
                  }
                >
                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography level="body-sm" sx={{ color: '#7B7F85' }}>{step.title}</Typography>
                  </Box>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Typography level="body-lg">
                {steps[currentStep - 1].title}
              </Typography>
            </Box>
          </Box>

          {/* Error display */}
          {error && (
            <Alert color="danger" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step content */}
          <Box sx={{ 
            bgcolor: 'white', 
            borderRadius: '16px', 
            p: 4, 
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)' 
          }}>
            <Stack spacing={4}>
              {/* Step content */}
              {renderStepContent()}

              {/* Navigation buttons */}
              <Stack direction="row" spacing={2} justifyContent="space-between">
                {/* Cancel/Back button logic */}
                {(currentStep === 1 || currentStep === 2) && hasHistory && (
                  <Button
                    variant="plain"
                    onClick={handleCancel}
                    sx={{
                      color: '#7B7F85',
                      '&:hover': {
                        color: '#1A1B1D',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                )}
                
                {currentStep === 3 && (
                  <Button
                    variant="plain"
                    onClick={handleCancel}
                    sx={{
                      color: '#7B7F85',
                      '&:hover': {
                        color: '#1A1B1D',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    Back
                  </Button>
                )}
                
                {/* Step 4 has no cancel/back button */}

                <Button
                  variant="solid"
                  onClick={handleNext}
                  disabled={!canProceed() || loading}
                  sx={{
                    backgroundColor: '#2e2d7e',
                    '&:hover': { backgroundColor: '#1e1d6e' },
                    '&:disabled': { backgroundColor: '#7B7F85' },
                    ml: 'auto' // Push to right when no cancel button
                  }}
                >
                  {loading ? (
                    <CircularProgress size="sm" color="neutral" />
                  ) : (
                    getButtonText()
                  )}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default PostJobWorkflow;
